import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState({
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token'),
  });

  // Configure api defaults
  useEffect(() => {
    if (tokens.access) {
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [tokens.access]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (tokens.access) {
        try {
          const response = await api.get('/api/auth/me/');
          setUser(response.data);
        } catch (error) {
          // Token might be expired, try to refresh
          if (tokens.refresh) {
            try {
              await refreshAccessToken();
            } catch (refreshError) {
              // Refresh failed, clear tokens
              logout();
            }
          } else {
            logout();
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const refreshAccessToken = async () => {
    try {
      const response = await api.post('/api/auth/refresh/', {
        refresh: tokens.refresh,
      });
      
      const newAccessToken = response.data.access;
      setTokens(prev => ({ ...prev, access: newAccessToken }));
      localStorage.setItem('access_token', newAccessToken);
      
      return newAccessToken;
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/auth/login/', {
        email,
        password,
      });

      const { user: userData, tokens: newTokens } = response.data;
      
      setUser(userData);
      setTokens(newTokens);
      
      localStorage.setItem('access_token', newTokens.access);
      localStorage.setItem('refresh_token', newTokens.refresh);
      
      api.defaults.headers.common['Authorization'] = `Bearer ${newTokens.access}`;
      
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/api/auth/register/', userData);
      
      // Auto-login after registration
      const loginResult = await login(userData.email, userData.password);
      return loginResult;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
      };
    }
  };

  const logout = () => {
    setUser(null);
    setTokens({ access: null, refresh: null });
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete api.defaults.headers.common['Authorization'];
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.patch('/api/auth/profile/', profileData);
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Profile update failed',
      };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      await api.post('/api/auth/change-password/', passwordData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Password change failed',
      };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isPremium: !!user?.is_premium,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
